import { Bell, AlertTriangle, CheckCircle2, Clock, DoorOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const timeAgo = (ts) => {
  if (!ts) return '';
  const then = new Date(ts).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const BELL_ICON = {
  ready: { Icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-500' },
  cleaning: { Icon: Clock, cls: 'bg-amber-50 text-amber-500' },
  checkout: { Icon: DoorOpen, cls: 'bg-blue-50 text-blue-500' },
};

export default function HomeNotifications({ data, open, onToggle, innerRef }) {
  const items = open === 'bell' ? data.bell : open === 'warning' ? data.warning : [];

  const IconButton = ({ type, Icon, dotClass, unseen, testid }) => (
    <button
      onClick={() => onToggle(type)}
      className={`relative w-10 h-10 rounded-full bg-white border flex items-center justify-center shadow-sm transition-all active:scale-95 ${
        open === type ? 'border-slate-300 ring-2 ring-slate-100' : 'border-slate-100 hover:border-slate-200'
      }`}
      data-testid={testid}
      aria-label={type === 'bell' ? 'Notifications' : 'Important alerts'}
    >
      <Icon size={18} className="text-slate-600" />
      {unseen > 0 && (
        <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${dotClass}`} data-testid={`${testid}-dot`} />
      )}
    </button>
  );

  return (
    <div className="relative flex items-center gap-2 flex-shrink-0" ref={innerRef}>
      <IconButton type="warning" Icon={AlertTriangle} dotClass="bg-red-500" unseen={data.warning_unseen} testid="notif-warning" />
      <IconButton type="bell" Icon={Bell} dotClass="bg-orange-500" unseen={data.bell_unseen} testid="notif-bell" />

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 z-50 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
            data-testid={`notif-panel-${open}`}
          >
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              {open === 'warning'
                ? <AlertTriangle size={16} className="text-red-500" />
                : <Bell size={16} className="text-orange-500" />}
              <h3 className="font-bold text-slate-900 text-sm">
                {open === 'warning' ? 'Important alerts' : 'Notifications'}
              </h3>
              <span className="ml-auto text-xs text-slate-400">{items.length}</span>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-3">
                    {open === 'warning'
                      ? <AlertTriangle size={22} className="text-slate-300" />
                      : <Bell size={22} className="text-slate-300" />}
                  </div>
                  <p className="text-sm text-slate-400">
                    {open === 'warning' ? 'No cleaning overruns' : 'No notifications yet'}
                  </p>
                </div>
              ) : (
                <ul>
                  {items.map((it) => {
                    if (open === 'warning') {
                      return (
                        <li key={it.id} className="px-4 py-3 flex gap-3 border-b border-slate-50 last:border-0" data-testid={`notif-item-${it.id}`}>
                          <div className="w-9 h-9 rounded-xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle size={17} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-800 leading-snug">{it.title}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {it.actor ? `${it.actor} · ` : ''}{timeAgo(it.ts)}
                              {it.resolved ? ' · now ready' : ''}
                            </p>
                          </div>
                        </li>
                      );
                    }
                    const cfg = BELL_ICON[it.type] || BELL_ICON.checkout;
                    const { Icon, cls } = cfg;
                    return (
                      <li key={it.id} className="px-4 py-3 flex gap-3 border-b border-slate-50 last:border-0" data-testid={`notif-item-${it.id}`}>
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cls}`}>
                          <Icon size={17} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-800 leading-snug truncate">{it.title}</p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {it.actor ? `${it.actor} · ` : ''}{timeAgo(it.ts)}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
